import { alpha, Box, ButtonBase, Chip, styled } from '@mui/material';
import { Paragraph, Span } from 'components/Typography';
import { useEffect, useState, type FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SidebarAccordion from './SidebarAccordion';
import duotone from '../../icons/duotone';
import Education from '../../icons/Education';
import Building from '../../icons/City';
import { useTranslation } from 'react-i18next';
import { ReportIcon } from 'icons/report/reactIcons';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import WorkIcon from '@mui/icons-material/Work';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { checkCanViewShifts, checkSeasonalRole } from 'utils/checkToken';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import CalendarViewDayIcon from '@mui/icons-material/CalendarViewDay';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TimelineIcon from '@mui/icons-material/Timeline';
import FolderIcon from '@mui/icons-material/Folder';
import { useSeason } from 'contexts/SeasonContext';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HotelIcon from '@mui/icons-material/Hotel';
import SchoolIcon from '@mui/icons-material/School';
import CampaignIcon from '@mui/icons-material/Campaign';
import { tenantConfigApi } from 'api';

interface Active {
  active: any;
}
interface MenuItem {
  name?: string;
  path?: string;
  icon?: React.ElementType;
  type?: 'label' | 'extLink';
  label?: string;
  feature?: string;
  children?: MenuItem[];
  beta?: boolean;
}

interface Compact {
  compact: number;
}

type ActiveCompact = Active & Compact;

const NavItemButton = styled(ButtonBase)<Active>(({ theme, active }) => ({
  height: 44,
  width: '100%',
  borderRadius: 8,
  marginBottom: 4,
  padding: '0 18px',
  justifyContent: 'flex-start',
  transition: 'all 0.15s ease',
  ...(active && {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  }),
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

const ListLabel = styled(Paragraph)<Compact>(({ theme, compact }) => ({
  fontWeight: 700,
  fontSize: '12px',
  marginTop: '20px',
  marginLeft: '15px',
  marginBottom: '10px',
  textTransform: 'uppercase',
  transition: 'all 0.15s ease',
  color: theme.palette.text.secondary,
  ...(compact && { opacity: 0, width: 0 }),
}));

const ExternalLink = styled('a')(({ theme }) => ({
  overflow: 'hidden',
  whiteSpace: 'pre',
  marginBottom: '8px',
  textDecoration: 'none',
  color: theme.palette.text.primary,
}));

const StyledText = styled(Span)<ActiveCompact & { level?: number }>(
  ({ theme, compact, active, level }) => {
    const isRoot = level === 0;
    return {
      whiteSpace: 'nowrap',
      paddingLeft: '0.8rem',
      transition: 'all 0.15s ease',
      fontSize: isRoot ? '14px' : '13px',
      fontWeight: isRoot ? 600 : 500,
      color: active
        ? theme.palette.primary.main
        : isRoot
        ? theme.palette.text.primary
        : theme.palette.text.secondary,
      ...(compact && { opacity: 0, width: 0 }),
    };
  },
);

const BulletIcon = styled('div')<Active>(({ theme, active }) => ({
  width: 4,
  height: 4,
  marginLeft: '10px',
  marginRight: '8px',
  overflow: 'hidden',
  borderRadius: '50%',
  background: active ? theme.palette.primary.main : theme.palette.text.disabled,
  boxShadow: active ? `0px 0px 0px 3px ${theme.palette.primary[200]}` : 'none',
}));

const BadgeValue = styled(Box)<Compact>(({ compact, theme }) => ({
  color: 'white',
  fontSize: '12px',
  fontWeight: 500,
  padding: '1px 6px',
  overflow: 'hidden',
  borderRadius: '300px',
  backgroundColor: theme.palette.primary.main,
  display: compact ? 'none' : 'unset',
}));

const BetaChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.6rem',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
  height: '16px',
  '& .MuiChip-label': {
    padding: '0px 6px',
    fontSize: '0.6rem',
  },
}));

// Common icon style
const iconStyle = (active: any) => ({
  fontSize: 18,
  marginRight: '4px',
  color: active ? 'primary.main' : 'text.secondary',
});

// Sidebar container style to ensure proper scrolling behavior
const SidebarContainer = styled(Box)({
  height: '100%',
  overflowY: 'auto',
  marginBottom: '150px', // To avoid content being cut off by the scrollbar
  boxSizing: 'border-box',
});

// ---------------------------------------------------------------
interface MultiLevelMenuProps {
  sidebarCompact: boolean;
}

// ---------------------------------------------------------------

const MultiLevelMenu: FC<MultiLevelMenuProps> = ({ sidebarCompact }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { seasonId } = useSeason();
  const [role, setRole] = useState('');
  const [enabledFeatures, setEnabledFeatures] = useState<string[]>(['HR']);
  const setSeasonalRole = async () => {
    const newRole = await checkSeasonalRole(seasonId);
    if (newRole !== role) {
      switch (newRole) {
        case 'DEPCHIEF': {
          navigate('/employee/defaultUserPage');
          break;
        }
        case 'USER': {
          navigate('/employee/defaultUserPage');
          break;
        }
      }
    }
    setRole(newRole);
  };
  const filterMenuItems = (
    items: MenuItem[],
    features: string[],
  ): MenuItem[] => {
    return items
      .filter((item) => !item.feature || features.includes(item.feature))
      .map((item) => ({
        ...item,
        children: item.children
          ? filterMenuItems(item.children, features)
          : undefined,
      }))
      .filter((item) => item.children?.length || !item.children);
  };

  const fetchEnabledFeatures = async () => {
    const response: { data: any } = await tenantConfigApi.findConfigs();

    const localEnabledFeatures = [];
    if (response.data.isHRModuleActive) {
      localEnabledFeatures.push('HR');
    }
    if (response.data.isTurnoverModuleActive) {
      localEnabledFeatures.push('TURNOVER');
    }
    if (response.data.isTimeAttendanceActive) {
      localEnabledFeatures.push('TIME_ATTENDANCE');
    }
    localEnabledFeatures.push('BUILDING_SAFETY');

    setEnabledFeatures(localEnabledFeatures);
  };

  useEffect(() => {
    setSeasonalRole();
  }, [seasonId]);

  useEffect(() => {
    fetchEnabledFeatures();
  }, []);

  const managerItems: MenuItem[] = [
    {
      name: t('menu.newsletter.label'),
      path: '/dashboard/newsletter',
      icon: CampaignIcon,
      feature: 'HR',
      beta: true,
    },
    {
      name: t('menu.recruiting.label'),
      icon: duotone.PersonCircleCheck,
      feature: 'HR',
      children: [
        {
          name: t('menu.recruiting.hiringPipelines'),
          path: '/dashboard/recruitment/pipelines',
          icon: ViewKanbanIcon,
          feature: 'HR',
        },
        {
          name: t('menu.recruiting.emailTemplates'),
          path: '/dashboard/recruitment/email-templates',
          icon: EmailIcon,
          feature: 'HR',
        },
        {
          name: t('jobAdvertisements.collectionName'),
          icon: WorkIcon,
          path: '/dashboard/job-advertisements',
          feature: 'HR',
        },
        {
          name: t('menu.recruiting.talentPool'),
          path: '/dashboard/recruitment/talent-pool',
          icon: duotone.UserList,
          feature: 'HR',
        },
        {
          name: t('menu.recruiting.dashboard'),
          path: '/dashboard/recruitment/dashboard',
          icon: duotone.Dashboard,
          feature: 'HR',
        },
        {
          name: t('menu.recruiting.jobAdStats'),
          path: '/dashboard/recruitment/job-ad-stats',
          icon: VisibilityIcon,
          feature: 'HR',
        },
        {
          name: t('menu.recruiting.candidates'),
          path: '/dashboard/employees/candidates',
          icon: duotone.PersonCircleCheck,
          feature: 'HR',
        },
      ],
    },
    {
      name: 'Talents',
      icon: duotone.UserList,
      children: [
        {
          icon: duotone.UserList,
          name: t('menu.management.employees'),
          path: '/dashboard/employees/',
          children: [
            {
              icon: duotone.UserProfile,
              name: t('menu.management.submenus.employees.AllEmployees'),
              path: '/dashboard/employees/',
            },
            {
              icon: duotone.UserProfile,
              name: t('menu.management.submenus.employees.noSeasonEmployees'),
              path: '/dashboard/employees/all',
            },
            {
              icon: duotone.UserProfile,
              name: t('menu.management.submenus.employees.AnnualEmployees'),
              path: '/dashboard/employees/annualemployees',
              feature: 'HR',
            },
            {
              icon: duotone.UserProfile,
              name: t('menu.management.submenus.employees.SeasonalEmployees'),
              path: '/dashboard/employees/seasonalemployees',
              feature: 'HR',
            },
          ],
        },
        {
          icon: PendingActionsIcon,
          name: t('dashboard.expiration'),
          path: '/dashboard/expiration',
          feature: 'HR',
        },
        {
          icon: FolderIcon,
          name: t('publicDocuments'),
          path: '/dashboard/public-documents',
          feature: 'HR',
        },
        {
          name: 'Todos',
          icon: duotone.TodoList,
          path: '/dashboard/todos',
          feature: 'HR',
        },
        {
          name: t('menu.dashboard.details'),
          path: '/dashboard/employees/new',
          icon: duotone.PersonChalkboard,
        },
        {
          name: t('menu.apps.workShifts'),
          icon: duotone.Calender,
          path: '/dashboard/employees/calendar/false',
          feature: 'HR',
        },
        {
          name: t('report.reportname'),
          icon: ReportIcon,
          path: '/dashboard/employees/report',
          feature: 'HR',
        },
        {
          name: 'Timeline',
          icon: TimelineIcon,
          path: '/dashboard/employees/timeline',
          feature: 'HR',
        },
        {
          name: t('menu.apps.chat'),
          icon: duotone.Chat,
          path: '/dashboard/employees/communications',
          feature: 'HR',
        },
        {
          icon: duotone.DiagramProject,
          name: t('departments.employees.edit'),
          path: '/dashboard/employees-hierarchy',
        },
      ],
    },
    {
      name: t('menu.management.shift'),
      feature: 'HR',
      icon: duotone.Calender,
      children: [
        {
          name: t('menu.management.submenus.shift.assignment'),
          icon: ViewTimelineIcon,
          path: '/dashboard/shift-assignments-v2',
        },
        {
          name: t('menu.management.submenus.shift.template'),
          icon: CalendarViewDayIcon,
          path: '/dashboard/shift-template',
        },
        {
          name: t('menu.management.submenus.shift.calendar'),
          icon: duotone.Calender,
          path: '/dashboard/shift-template/calendar',
        },
        {
          name: t('shift.workedHours'),
          icon: AccessTimeIcon,
          path: '/dashboard/shift/employeesHours',
        },
        {
          name: t('shift.statistics'),
          icon: duotone.BarChart,
          path: '/dashboard/shift/statistics',
        },
        /* {
          name: t('shift.requests'),
          icon: WorkHistoryIcon,
          path: '/employee/shiftrequests',
          feature: 'HR',
        }, */
        {
          feature: 'TIME_ATTENDANCE',
          name: t('presenze.navItem'),
          icon: AccessTimeIcon,
          path: '/dashboard/presenze',
        },
        {
          feature: 'TIME_ATTENDANCE',
          name: t('presenze.approvals.navItem'),
          icon: PendingActionsIcon,
          path: '/dashboard/presenze/approvals',
        },
        {
          feature: 'TIME_ATTENDANCE',
          name: t('presenze.reports.navItem'),
          icon: duotone.BarChart,
          path: '/dashboard/presenze/reports',
        },
      ],
    },
    {
      name: t('training.settingsLabel'),
      icon: SchoolIcon,
      children: [
        {
          name: t('training.dashboard.menuLabel'),
          icon: duotone.Dashboard,
          path: '/dashboard/training/summary',
        },
        {
          name: t('training.courses.menuLabel'),
          icon: duotone.Accounts,
          path: '/dashboard/training/courses',
        },
        {
          name: t('training.settingsMenuLabel'),
          icon: duotone.Settings,
          path: '/dashboard/settings/training',
        },
      ],
    },
    {
      name: t('housing.title'),
      icon: HotelIcon,
      feature: 'HR',
      children: [
        {
          name: t('roomAssignments.title'),
          icon: HotelIcon,
          path: '/dashboard/room-assignments',
          feature: 'HR',
        },
      ],
    },
    {
      name: t('menu.management.label'),
      icon: duotone.Settings,
      children: [
        {
          name: t('menu.management.companyStructure.label'),
          icon: duotone.Settings,
          children: [
            {
              icon: WorkspacesIcon,
              name: t('department.segments'),
              path: '/dashboard/segments',
            },
            {
              icon: duotone.DiagramProject,
              name: t('menu.management.companyStructure.departments'),
              path: '/dashboard/departments',
            },
            {
              icon: Education,
              name: t('menu.management.companyStructure.experienceLevels'),
              path: '/dashboard/experience-levels',
              feature: 'HR',
            },
            {
              icon: Building,
              name:
                t('menu.management.companyStructure.buildings'),
              path: '/dashboard/buildings',
              feature: 'HR',
            },
          ],
        },
        {
          name: t('menu.management.seasons'),
          icon: duotone.ElementHub,
          path: '/dashboard/seasons',
        },
        {
          name: 'Todo Configuration',
          icon: duotone.Settings,
          path: '/dashboard/todos/config',
        },
        {
          name: t('menu.apps.paymentMethods'),
          icon: duotone.MoneyIcon,
          path: 'turnover/paymentMethods',
          feature: 'TURNOVER',
        },
        {
          name: t('menu.apps.paymentSections'),
          icon: duotone.Store,
          path: 'turnover/sections',
          feature: 'TURNOVER',
        },
        {
          name: t('menu.apps.buildingSafety'),
          icon: Building,
          feature: 'BUILDING_SAFETY',
          children: [
            {
              name: t('area.metaAreas'),
              icon: Building,
              path: '/dashboard/building-safety/meta-areas',
              feature: 'BUILDING_SAFETY',
              beta: true,
            },
            {
              name: t('menu.apps.areas'),
              icon: Building,
              path: '/dashboard/building-safety/areas',
              feature: 'BUILDING_SAFETY',
              beta: true,
            },
          ],
        },
      ],
    },
  ];

  const ownerItems: MenuItem[] = [
    {
      name: t('menu.apps.amounts'),
      feature: 'TURNOVER',
      icon: duotone.MoneyIcon,
      children: [
        {
          name: t('menu.apps.summary'),
          icon: duotone.RequestQuote,
          path: 'turnover/summary',
          feature: 'TURNOVER',
        },
        // {
        //   name: t('menu.apps.statistics'),
        //   icon: duotone.BarChart,
        //   path: 'turnover/statistics',
        //   feature: 'TURNOVER',
        // },
        {
          name: t('menu.apps.comparison'),
          icon: ViewTimelineIcon,
          path: 'turnover/comparison',
          feature: 'TURNOVER',
        },
      ],
    },
    {
      name: t('menu.apps.buildingSafety'),
      feature: 'BUILDING_SAFETY',
      icon: Building,
      children: [
        {
          name: t('menu.apps.safetySummary'),
          icon: duotone.Dashboard,
          path: '/dashboard/building-safety/summary',
          feature: 'BUILDING_SAFETY',
        },
        {
          name: t('menu.apps.floorplans'),
          icon: Building,
          path: '/dashboard/building-safety/floorplans',
          feature: 'BUILDING_SAFETY',
          beta: true,
        },
      ],
    },
  ];

  const controllerTurnoverItems: MenuItem[] = [
    {
      name: t('menu.apps.amounts'),
      feature: 'TURNOVER',
      icon: duotone.MoneyIcon,
      children: [
        {
          name: t('menu.apps.summary'),
          icon: duotone.RequestQuote,
          path: 'turnover/summary',
          feature: 'TURNOVER',
        },
        // {
        //   name: t('menu.apps.statistics'),
        //   icon: duotone.BarChart,
        //   path: 'turnover/statistics',
        //   feature: 'TURNOVER',
        // },
        {
          name: t('menu.apps.comparison'),
          icon: ViewTimelineIcon,
          path: 'turnover/comparison',
          feature: 'TURNOVER',
        },
      ],
    },
  ];
  const depChiefItems: MenuItem[] = [
    {
      name: 'Home',
      icon: duotone.House,
      path: '/employee/defaultUserPage',
    },
    {
      feature: 'HR',
      name: t('menu.apps.workShifts'),
      icon: duotone.Calender,
      path: '/dashboard/employees/calendar/false',
    },
    {
      name: t('user.personalInfo'),
      icon: duotone.UserProfile,
      path: '/employee/info',
    },
    {
      feature: 'HR',
      icon: FolderIcon,
      name: t('publicDocuments'),
      path: '/dashboard/public-documents',
    },
    {
      feature: 'HR',
      icon: duotone.DiagramProject,
      name: t('departments.employees.edit'),
      path: '/dashboard/employees-hierarchy',
    },
    {
      feature: 'HR',
      name: 'Todos',
      icon: duotone.TodoList,
      path: '/employee/todos',
    },
    {
      feature: 'HR',
      name: t('menu.management.shift'),
      icon: WorkHistoryIcon,
      children: [
        {
          name: t('menu.management.submenus.shift.template'),
          icon: CalendarViewDayIcon,
          path: '/dashboard/shift-template',
        },
        {
          name: t('menu.management.submenus.shift.calendar'),
          icon: duotone.Calender,
          path: '/dashboard/shift-template/calendar',
        },
        {
          name: t('menu.management.submenus.shift.assignment'),
          icon: ViewTimelineIcon,
          path: '/dashboard/shift-assignments-v2',
        },
      ],
    },
    /* {
      feature: 'HR',
      name: t('shift.requests'),
      icon: WorkHistoryIcon,
      path: '/employee/shiftrequests',
    }, */
    {
      feature: 'TIME_ATTENDANCE',
      name: t('presenze.navItem'),
      icon: AccessTimeIcon,
      path: '/dashboard/presenze',
    },
  ];
  const userItems: MenuItem[] = [
    {
      name: 'Home',
      icon: duotone.House,
      path: '/employee/defaultUserPage',
    },
    {
      name: t('user.personalInfo'),
      icon: duotone.UserProfile,
      path: '/employee/info',
    },
    {
      icon: FolderIcon,
      name: t('publicDocuments'),
      path: '/dashboard/public-documents',
    },
    {
      feature: 'HR',
      name: t('menu.apps.workShifts'),
      icon: duotone.Calender,
      path: '/employee/calendar',
    },
    {
      feature: 'HR',
      name: 'Todos',
      icon: duotone.TodoList,
      path: '/employee/todos',
    },
    /* {
      feature: 'HR',
      name: t('shift.requests'),
      icon: WorkHistoryIcon,
      path: '/employee/shiftrequests',
    }, */
    /* {
      name: t('menu.apps.chat'),
      icon: duotone.Chat,
      path: '/employee/chat'
    }, */
    /* {
      name: t("menu.management.shift"),
      icon: WorkHistoryIcon,
      path: '/employee/calendar'
    } */
  ];

  // Lift the Shift submenu out of managerItems so we can show it to a USER
  // who has the cross-season `canViewShifts` permission (read-only access
  // is enforced at the page/API layer).
  const shiftSubmenu = managerItems.find(
    (item) => item.name === t('menu.management.shift'),
  );
  const userItemsWithShiftView: MenuItem[] =
    checkCanViewShifts() && shiftSubmenu
      ? [...userItems, shiftSubmenu]
      : userItems;

  const navigations = (() => {
    switch (role) {
      case 'DEPCHIEF':
        return filterMenuItems(depChiefItems, enabledFeatures);
      case 'USER':
        return filterMenuItems(userItemsWithShiftView, enabledFeatures);
      case 'DEVELOPER':
      case 'MANAGER':
        return filterMenuItems(managerItems, enabledFeatures);
      case 'CONTROLLER':
        return filterMenuItems(
          [...managerItems, ...controllerTurnoverItems],
          enabledFeatures,
        );
      case 'OWNER':
        return filterMenuItems(
          [...ownerItems, ...managerItems],
          enabledFeatures,
        );
      default:
        return [];
    }
  })();

  const [activeAccordions, setActiveAccordions] = useState<
    Record<number, string>
  >({});

  const handleAccordionToggle = (
    level: number,
    id: string,
    isOpening: boolean,
  ) => {
    setActiveAccordions((prev) => {
      if (isOpening && prev[level] === id) return prev;
      if (!isOpening && prev[level] !== id) return prev;

      const clone = { ...prev };
      if (isOpening) {
        clone[level] = id;
      } else {
        clone[level] = '';
      }
      return clone;
    });
  };

  let longestMatch = '';
  const findLongestMatch = (items: MenuItem[]) => {
    for (const item of items) {
      if (item.path) {
        const p = item.path;
        const checkPath = p.startsWith('/') ? p : `/${p}`;

        if (checkPath === '/dashboard' && pathname !== '/dashboard') {
          // strict match for dashboard root
        } else if (
          checkPath === '/employee/defaultUserPage' &&
          pathname !== '/employee/defaultUserPage'
        ) {
          // strict match for employee root
        } else if (pathname.startsWith(checkPath)) {
          const followChar = pathname.charAt(checkPath.length);
          if (!followChar || followChar === '/' || followChar === '?') {
            if (p.length > longestMatch.length) longestMatch = p;
          }
        } else if (pathname.includes(p)) {
          if (p.length > longestMatch.length) longestMatch = p;
        }
      }
      if (item.children) {
        findLongestMatch(item.children);
      }
    }
  };
  findLongestMatch(navigations);

  // handle active current page
  const activeRoute = (path?: string) => {
    if (!path) return 0;
    if (path === longestMatch) return 1;
    if (pathname === path || pathname === `/${path}`) return 1;
    return 0;
  };
  // handle navigate to another route or page
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // ACTIVATE SIDEBAR COMPACT
  const COMPACT = sidebarCompact ? 1 : 0;

  //   RECURSIVE FUNCTION TO RENDER MULTI LEVEL MENU
  const renderLevels = (data: any, level: number = 0) => {
    return data.map((item: any, index: any) => {
      if (item.type === 'label')
        return (
          <ListLabel key={index} compact={COMPACT}>
            {item.label}
          </ListLabel>
        );

      if (item.children) {
        return (
          <SidebarAccordion
            key={index}
            item={item}
            sidebarCompact={COMPACT}
            level={level}
            activeSibling={activeAccordions[level]}
            nodeId={`${level}-${index}`}
            onToggleSibling={(id: string, isOpening: boolean) =>
              handleAccordionToggle(level, id, isOpening)
            }
          >
            {renderLevels(item.children, level + 1)}
          </SidebarAccordion>
        );
      } else if (item.type === 'extLink') {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            rel='noopener noreferrer'
            target='_blank'
          >
            <NavItemButton key={item.name} name='child' active={0}>
              {(() => {
                if (item.icon) {
                  return <item.icon sx={iconStyle(0)} />;
                } else {
                  return (
                    <span className='item-icon icon-text'>{item.iconText}</span>
                  );
                }
              })()}

              <StyledText
                compact={COMPACT}
                active={activeRoute(item.path)}
                level={level}
              >
                {item.name}
              </StyledText>

              <Box mx='auto' />

              {item.beta && !COMPACT && <BetaChip label='BETA' size='small' />}

              {item.badge && (
                <BadgeValue compact={COMPACT}>{item.badge.value}</BadgeValue>
              )}
            </NavItemButton>
          </ExternalLink>
        );
      } else {
        return (
          <Box key={index}>
            <NavItemButton
              key={item.name}
              className='navItem'
              active={activeRoute(item.path)}
              onClick={() => {
                handleNavigation(item.path);
              }}
            >
              {item?.icon ? (
                <item.icon sx={iconStyle(activeRoute(item.path))} />
              ) : (
                <BulletIcon active={activeRoute(item.path)} />
              )}

              <StyledText compact={COMPACT} active={activeRoute(item.path)}>
                {item.name}
              </StyledText>

              <Box mx='auto' />

              {item.beta && !COMPACT && <BetaChip label='BETA' size='small' />}

              {item.badge && (
                <BadgeValue compact={COMPACT}>{item.badge.value}</BadgeValue>
              )}
            </NavItemButton>
          </Box>
        );
      }
    });
  };

  return <SidebarContainer>{renderLevels(navigations)}</SidebarContainer>;
};

export default MultiLevelMenu;
